import { useForm } from '@tanstack/react-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { AuthLayout } from '@cold-ambulance/layout';
import { Button, CardContent, Input, Label } from '@cold-ambulance/shadcn';
import {
  EmailInput,
  FieldMessage,
  FormMessage,
  errorToFormIssue,
  useAction,
} from '@cold-ambulance/ui';

import { type Step, StepCircles } from './stepper.tsx';

const schema = z.object({
  signatoryFirstName: z
    .string()
    .min(1, { message: 'الاسم الأول للممثل مطلوب' }),
  signatoryLastName: z
    .string()
    .min(1, { message: 'الاسم الأخير للممثل مطلوب' }),
  signatoryJobTitle: z
    .string()
    .min(1, { message: 'المنصب مطلوب' }),
  signatoryEmail: z
    .string()
    .min(1, { message: 'البريد الإلكتروني للممثل مطلوب' })
    .email({ message: 'البريد الإلكتروني غير صحيح' }),
});

export default function SignupSignatory({
  step,
  index,
  onBack,
}: {
  step: Step;
  index: number;
  onBack?: () => void;
}) {
  const navigate = useNavigate();
  const updateVendorMutation = useAction('PATCH /vendor/me', {
    onSuccess: () => navigate('/'),
  });

  const form = useForm({
    defaultValues: {
      signatoryFirstName: '',
      signatoryLastName: '',
      signatoryJobTitle: '',
      signatoryEmail: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await updateVendorMutation.mutateAsync(value);
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <AuthLayout title="سجل معنا" className="border" onBack={onBack}>
      <StepCircles step={step} index={index} />
      <p className="mt-6 font-bold">جهة الشخص المفوض بالتوقيع</p>
      <p className="text-muted-foreground text-sm">
        (المفوض بالتوقيع نيابة عن الشركة)
      </p>

      <CardContent className="px-0">
        <form
          className="mt-4 mb-4 space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
            <form.Field
              name="signatoryFirstName"
              children={(field) => (
                <div className="grid w-full items-center gap-2">
                  <Label className="text-sm" htmlFor="signatoryFirstName">
                    الاسم الأول
                  </Label>
                  <Input
                    id="signatoryFirstName"
                    type="text"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />
            <form.Field
              name="signatoryLastName"
              children={(field) => (
                <div className="grid w-full items-center gap-2">
                  <Label className="text-sm" htmlFor="signatoryLastName">
                    الاسم الأخير
                  </Label>
                  <Input
                    id="signatoryLastName"
                    type="text"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />
          </div>

          <form.Field
            name="signatoryJobTitle"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="signatoryJobTitle">
                  المنصب
                </Label>
                <Input
                  id="signatoryJobTitle"
                  type="text"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="signatoryEmail"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="signatoryEmail">
                  البريد الإلكتروني
                </Label>
                <EmailInput
                  id="signatoryEmail"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  سيتم إرسال رابط توقيع العقد إلى هذا البريد عند إبرام العقد
                </p>
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="mt-8 w-full cursor-pointer"
              >
                {isSubmitting ? 'جاري الإرسال...' : 'إنهاء التسجيل'}
              </Button>
            )}
          />
          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </AuthLayout>
  );
}
